import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Trash2, Loader2, ShieldCheck, Lock, BellRing, Smartphone, Monitor } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AdminPager } from "@/components/admin/AdminPager";
import { fmtInt, fmtDate, fmtInactive } from "@/lib/activityFmt";

// Lot 765 — inactifs depuis N jours (N réglable ici, mémorisé pour tout le monde par le super admin) + suppression manuelle.
const ROLE_LABELS = { user: "Membre", preacher: "Prédicateur", artist: "Artiste", influencer: "Influenceur", moderator: "Modérateur", manager: "Manager", associate: "Associé", support: "Support", super_admin: "Super admin" };
const initials = (name) => (name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

const StatusChip = ({ r }) => {
  if (r.protected) {
    return <span className="chip text-[10px] inline-flex items-center gap-1" style={{ background: "#EEF0F3", color: "#475467" }} title="Jamais supprimé automatiquement" data-testid={`inactive-protected-${r.id}`}>
      {r.protected === "verified" ? <ShieldCheck size={11} /> : <Lock size={11} />} {r.protected_label}
    </span>;
  }
  if (r.warned_at) {
    return <span className="chip text-[10px] inline-flex items-center gap-1" style={{ background: "#FEF3C7", color: "#92400E" }} data-testid={`inactive-warned-${r.id}`}>
      <BellRing size={11} /> Averti · suppression le {fmtDate(r.delete_at)}
    </span>;
  }
  return <span className="text-xs opacity-40">—</span>;
};

const Row = ({ r, isSuper, checked, onCheck, onDelete }) => (
  <tr className="border-t" style={{ borderColor: "var(--yalola-border)" }} data-testid={`inactive-row-${r.id}`}>
    {isSuper && <td className="px-3 py-2">{!r.manual_blocked && <Checkbox checked={checked} onCheckedChange={(v) => onCheck(r.id, !!v)} aria-label={`Sélectionner ${r.name}`} data-testid={`inactive-check-${r.id}`} />}</td>}
    <td className="px-3 py-2">
      <div className="flex items-center gap-2 min-w-0">
        <Avatar className="h-8 w-8 shrink-0"><AvatarImage src={r.avatar_url || undefined} alt="" /><AvatarFallback className="text-[10px]">{initials(r.name)}</AvatarFallback></Avatar>
        <div className="min-w-0">
          <Link to={`/profile/${r.id}`} className="font-medium text-sm truncate block hover:underline" data-testid={`inactive-name-${r.id}`}>{r.name || "Sans nom"}</Link>
          <div className="text-[11px] truncate" style={{ color: "var(--yalola-text-muted)" }}>{r.email || r.phone || "—"}</div>
        </div>
      </div>
    </td>
    <td className="px-3 py-2"><div className="flex flex-wrap gap-1">{(r.roles || []).map((x) => <span key={x} className="chip text-[10px]">{ROLE_LABELS[x] || x}</span>)}</div></td>
    <td className="px-3 py-2 text-xs tabular-nums whitespace-nowrap">{fmtDate(r.created_at)}</td>
    <td className="px-3 py-2 text-xs whitespace-nowrap" data-testid={`inactive-last-${r.id}`}>
      <div className="flex items-center gap-1">
        {r.last_platform === "web" ? <Monitor size={11} /> : r.last_platform ? <Smartphone size={11} /> : null}
        <span>{fmtDate(r.last_seen_at)}</span>
      </div>
      <div style={{ color: "var(--yalola-text-muted)" }}>{fmtInactive(r)}</div>
    </td>
    <td className="px-3 py-2"><StatusChip r={r} /></td>
    {isSuper && <td className="px-3 py-2 text-right">
      {!r.manual_blocked && <Button size="sm" variant="outline" className="rounded-full h-7 px-2.5" style={{ color: "#B42318" }} onClick={() => onDelete([r.id])} data-testid={`inactive-delete-${r.id}`}><Trash2 size={13} className="mr-1" /> Supprimer</Button>}
    </td>}
  </tr>
);

export const InactiveListCard = ({ isSuper, config, onConfig, onDeleted }) => {
  const [days, setDays] = useState(String(config.list_days || 30));
  const [applied, setApplied] = useState(config.list_days || 30);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [confirmIds, setConfirmIds] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (p = page, d = applied, query = q, pp = perPage) => {
    setLoading(true);
    try {
      const { data: res } = await api.get(`/admin/activity/inactive?days=${d}&page=${p}&per_page=${pp}&q=${encodeURIComponent(query.trim())}`);
      setData(res); setPage(res.page);
    } catch (e) { toast.error(e?.friendlyMessage || "Chargement impossible"); }
    setLoading(false);
  }, [page, applied, q, perPage]);
  useEffect(() => { load(1, applied, "", 50); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const goPage = (p) => { setSelected(new Set()); load(p, applied, q); };
  const changePerPage = (pp) => { setPerPage(pp); setSelected(new Set()); load(1, applied, q, pp); };

  const apply = async () => {
    const n = Math.max(1, Math.min(3650, parseInt(days, 10) || 30));
    setDays(String(n)); setApplied(n); setSelected(new Set());
    await load(1, n, q);
    if (isSuper && n !== config.list_days) {
      try { const { data: cfg } = await api.put("/admin/activity/config", { list_days: n }); onConfig(cfg); toast.success(`Seuil mémorisé : ${n} jours`); }
      catch (e) { toast.error(e?.friendlyMessage || "Seuil non mémorisé"); }
    }
  };
  const check = (id, on) => setSelected((s) => { const n = new Set(s); if (on) n.add(id); else n.delete(id); return n; });
  const selectable = (data?.rows || []).filter((r) => !r.manual_blocked);
  const allChecked = selectable.length > 0 && selectable.every((r) => selected.has(r.id));
  const checkAll = (on) => setSelected(on ? new Set(selectable.map((r) => r.id)) : new Set());
  const doDelete = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/admin/activity/delete", { ids: confirmIds });
      toast.success(`${res.deleted.length} compte(s) supprimé(s)${res.skipped.length ? ` · ${res.skipped.length} ignoré(s) (protégés)` : ""}`);
      setConfirmIds(null); setSelected(new Set());
      await load(page, applied, q);
      onDeleted?.();
    } catch (e) { toast.error(e?.friendlyMessage || "Suppression impossible"); }
    setBusy(false);
  };

  return (
    <div className="yalola-card p-4" data-testid="inactive-card">
      <div className="flex items-end gap-2 flex-wrap mb-3">
        <div>
          <div className="font-bold font-display">Inactifs depuis</div>
          <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>Aucune ouverture de l'app ni du site depuis au moins N jours (jamais revus : depuis l'inscription).</div>
        </div>
        <div className="flex items-center gap-1.5 ml-auto">
          <Input type="number" min={1} max={3650} value={days} onChange={(e) => setDays(e.target.value)} onKeyDown={(e) => e.key === "Enter" && apply()} className="h-9 w-24 rounded-full text-center tabular-nums" aria-label="Nombre de jours" data-testid="inactive-days-input" />
          <span className="text-sm">jours</span>
          <Button size="sm" className="pill-primary rounded-full h-9" onClick={apply} disabled={loading} data-testid="inactive-days-apply">Appliquer</Button>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--yalola-text-muted)" }} />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(1, applied, q)} placeholder="Rechercher un nom, un e-mail ou un numéro…" className="h-9 pl-8 rounded-full" data-testid="inactive-search" />
        </div>
        <span className="text-xs tabular-nums" style={{ color: "var(--yalola-text-muted)" }} data-testid="inactive-total">
          {data ? <><b>{fmtInt(data.total)}</b> membre(s) inactif(s) depuis {data.days} jours ou plus</> : "…"}
        </span>
        {isSuper && selected.size > 0 && (
          <Button size="sm" variant="destructive" className="rounded-full h-8" onClick={() => setConfirmIds([...selected])} data-testid="inactive-delete-selected">
            <Trash2 size={13} className="mr-1" /> Supprimer la sélection ({selected.size})
          </Button>
        )}
      </div>
      <div className="overflow-x-auto rounded-xl border" style={{ borderColor: "var(--yalola-border)" }}>
        <table className="w-full text-sm" data-testid="inactive-table">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>
              {isSuper && <th className="px-3 py-2 w-8"><Checkbox checked={allChecked} onCheckedChange={(v) => checkAll(!!v)} aria-label="Tout sélectionner" disabled={!selectable.length} data-testid="inactive-check-all" /></th>}
              <th className="px-3 py-2 font-semibold">Membre</th><th className="px-3 py-2 font-semibold">Rôle</th><th className="px-3 py-2 font-semibold">Inscrit le</th>
              <th className="px-3 py-2 font-semibold">Dernière activité</th><th className="px-3 py-2 font-semibold">Statut</th>{isSuper && <th className="px-3 py-2" />}
            </tr>
          </thead>
          <tbody>
            {(data?.rows || []).map((r) => <Row key={r.id} r={r} isSuper={isSuper} checked={selected.has(r.id)} onCheck={check} onDelete={setConfirmIds} />)}
            {data && data.rows.length === 0 && <tr><td colSpan={isSuper ? 7 : 5} className="px-3 py-8 text-center text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="inactive-empty">Aucun membre inactif depuis {data.days} jours{q ? ` pour « ${q} »` : ""}.</td></tr>}
            {!data && <tr><td colSpan={isSuper ? 7 : 5} className="px-3 py-8 text-center"><Loader2 size={18} className="animate-spin inline" /></td></tr>}
          </tbody>
        </table>
      </div>
      {data && <div className="mt-3 flex items-center justify-between gap-2 flex-wrap" data-testid="inactive-pagination">
        <span className="text-xs tabular-nums" style={{ color: "var(--yalola-text-muted)" }} data-testid="inactive-page-info">
          Page <b>{data.page}</b> / {data.pages} · {data.rows.length} affiché(s) sur {fmtInt(data.total)}
        </span>
        <AdminPager page={data.page} pages={data.pages} onPage={goPage} testId="inactive-pager" />
        <label className="text-xs flex items-center gap-1.5" style={{ color: "var(--yalola-text-muted)" }}>
          Par page
          <select value={perPage} onChange={(e) => changePerPage(Number(e.target.value))} className="h-8 rounded-full border px-2 text-xs bg-transparent" style={{ borderColor: "var(--yalola-border)", color: "var(--yalola-text)" }} aria-label="Lignes par page" data-testid="inactive-per-page">
            {[25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
      </div>}
      <Dialog open={!!confirmIds} onOpenChange={(o) => !o && !busy && setConfirmIds(null)}>
        <DialogContent className="sm:max-w-md" data-testid="inactive-delete-dialog">
          <DialogHeader>
            <DialogTitle>Supprimer {confirmIds?.length === 1 ? "ce compte" : `${confirmIds?.length} comptes`} ?</DialogTitle>
            <DialogDescription>Suppression définitive : compte, publications, stories, commentaires, replays et médias. Les comptes protégés (rôle admin, solde à verser) sont ignorés. Cette action est irréversible.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" className="rounded-full" onClick={() => setConfirmIds(null)} disabled={busy} data-testid="inactive-delete-cancel">Annuler</Button>
            <Button variant="destructive" className="rounded-full" onClick={doDelete} disabled={busy} data-testid="inactive-delete-confirm">{busy ? <Loader2 size={14} className="animate-spin mr-1" /> : <Trash2 size={14} className="mr-1" />} Supprimer définitivement</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
